import React, { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    updateProfile,
    sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";

const AuthModal = ({ mode: initialMode, onClose, onSwitchMode }) => {
    const [mode, setMode] = useState(initialMode);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [disciplines, setDisciplines] = useState([]);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        role: 'candidate',
        discipline: '',
        years_of_experience: ''
    });

    useEffect(() => {
        fetchDisciplines();
    }, []);

    const fetchDisciplines = async () => {
        try {
            const disciplinesRef = collection(db, 'disciplines');
            const disciplinesSnapshot = await getDocs(disciplinesRef);
            const disciplinesList = disciplinesSnapshot.docs.map(doc => doc.data().name);
            setDisciplines(disciplinesList.length ? disciplinesList : ['Civil', 'Mechanical', 'Electrical', 'Chemical', 'Industrial']);
        } catch (error) {
            console.error('Failed to fetch disciplines:', error);
            // Set default disciplines if fetch fails
            setDisciplines(['Civil', 'Mechanical', 'Electrical', 'Chemical', 'Industrial']);
        }
    };

    const saveUserToFirestore = async (userId, userData) => {
        try {
            const userRef = doc(db, 'users', userId);
            await setDoc(userRef, {
                ...userData,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                email_verified: false,
                assessment_count: 0,
                is_active: true
            });
        } catch (error) {
            console.error('Error saving user to Firestore:', error);
            throw new Error('Failed to save user data');
        }
    };

    const handleLogin = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Get user role from Firestore
            const userRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userRef);

            if (!userDoc.exists()) {
                throw new Error('User data not found');
            }

            const userData = userDoc.data();

            // Check if email is verified (optional - can be removed if not required)
            if (!user.emailVerified) {
                toast.warning('Please verify your email before logging in. Check your inbox for verification link.');
                // Optionally resend verification email
                await sendEmailVerification(user);
                return null;
            }

            toast.success(`Welcome back, ${userData.full_name || user.email}!`);

            // Redirect based on role
            const redirectPath = userData.role === 'admin' ? '/admin' :
                userData.role === 'mentor' ? '/mentor' : '/dashboard';
            navigate(redirectPath);

            return userData;
        } catch (error) {
            console.error('Login error:', error);
            let errorMessage = 'Login failed';
            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many failed attempts. Please try again later';
                    break;
                default:
                    errorMessage = error.message || 'Authentication failed';
            }
            toast.error(errorMessage);
            return null;
        }
    };

    const handleRegister = async (userData) => {
        try {
            // Create user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                userData.email,
                userData.password
            );
            const user = userCredential.user;

            // Update user profile with display name
            await updateProfile(user, {
                displayName: userData.full_name
            });

            // Send email verification
            await sendEmailVerification(user);

            // Prepare user data for Firestore
            const firestoreUserData = {
                uid: user.uid,
                email: userData.email,
                full_name: userData.full_name,
                role: userData.role,
                discipline: userData.discipline || null,
                years_of_experience: userData.years_of_experience ? parseInt(userData.years_of_experience) : null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                email_verified: false,
                assessment_count: 0,
                is_active: true
            };

            // Save user data to Firestore
            await saveUserToFirestore(user.uid, firestoreUserData);

            toast.success('Registration successful! Please check your email to verify your account.');

            // Redirect based on role
            const redirectPath = userData.role === 'admin' ? '/admin' :
                userData.role === 'mentor' ? '/mentor' : '/dashboard';
            navigate(redirectPath);

            return firestoreUserData;
        } catch (error) {
            console.error('Registration error:', error);
            let errorMessage = 'Registration failed';
            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'An account already exists with this email';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password should be at least 6 characters';
                    break;
                default:
                    errorMessage = error.message || 'Registration failed';
            }
            toast.error(errorMessage);
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate passwords match for registration
        if (mode === 'register' && formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        // Validate password strength for registration
        if (mode === 'register' && formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            if (mode === 'login') {
                await handleLogin(formData.email, formData.password);
                onClose();
            } else {
                const userData = {
                    email: formData.email,
                    password: formData.password,
                    full_name: formData.full_name,
                    role: formData.role,
                    discipline: formData.discipline || null,
                    years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null
                };
                const result = await handleRegister(userData);
                if (result) {
                    onClose();
                }
            }
        } catch (error) {
            // Error already handled in individual functions
            console.error('Authentication error:', error);
        } finally {
            setLoading(false);
        }
    };

    const switchMode = (newMode) => {
        setMode(newMode);
        onSwitchMode(newMode);
        // Reset form when switching modes
        setFormData({
            email: '',
            password: '',
            confirmPassword: '',
            full_name: '',
            role: 'candidate',
            discipline: '',
            years_of_experience: ''
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="auth-modal">
            <div className="bg-white rounded-sm w-full max-w-md p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={onClose}
                    data-testid="close-auth-modal-btn"
                    className="absolute right-4 top-4 text-[#666666] hover:text-[#0A0A0A] transition-colors"
                >
                    <X size={24} />
                </button>

                <div className="mb-6">
                    <h2 className="font-heading text-2xl font-semibold text-[#0A0A0A] mb-2">
                        {mode === 'login' ? 'Sign In' : 'Create Account'}
                    </h2>
                    <p className="text-sm text-[#666666]">
                        {mode === 'login'
                            ? 'Welcome back! Enter your credentials to continue.'
                            : 'Start your journey toward professional engineer registration.'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'register' && (
                        <>
                            <div>
                                <Label htmlFor="full_name" className="text-sm font-medium text-[#0A0A0A]">Full Name *</Label>
                                <Input
                                    id="full_name"
                                    data-testid="input-full-name"
                                    type="text"
                                    required
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                                    className="mt-1 rounded-sm border-[#DCDCDA] focus:border-[#002FA7] focus:ring-[#002FA7]/20"
                                />
                            </div>

                            <div>
                                <Label htmlFor="role" className="text-sm font-medium text-[#0A0A0A]">I am a *</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(value) => setFormData({...formData, role: value})}
                                >
                                    <SelectTrigger data-testid="select-role" className="mt-1 rounded-sm border-[#DCDCDA]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="candidate" data-testid="role-candidate">Candidate Engineer</SelectItem>
                                        <SelectItem value="mentor" data-testid="role-mentor">Mentor</SelectItem>
                                        <SelectItem value="admin" data-testid="role-admin">Administrator</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="discipline" className="text-sm font-medium text-[#0A0A0A]">Engineering Discipline</Label>
                                <Select
                                    value={formData.discipline}
                                    onValueChange={(value) => setFormData({...formData, discipline: value})}
                                >
                                    <SelectTrigger data-testid="select-discipline" className="mt-1 rounded-sm border-[#DCDCDA]">
                                        <SelectValue placeholder="Select your discipline" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {disciplines.map(disc => (
                                            <SelectItem key={disc} value={disc}>{disc}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="years_of_experience" className="text-sm font-medium text-[#0A0A0A]">Years of Experience</Label>
                                <Input
                                    id="years_of_experience"
                                    data-testid="input-experience"
                                    type="number"
                                    min="0"
                                    max="50"
                                    value={formData.years_of_experience}
                                    onChange={(e) => setFormData({...formData, years_of_experience: e.target.value})}
                                    className="mt-1 rounded-sm border-[#DCDCDA] focus:border-[#002FA7] focus:ring-[#002FA7]/20"
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <Label htmlFor="email" className="text-sm font-medium text-[#0A0A0A]">Email Address *</Label>
                        <Input
                            id="email"
                            data-testid="input-email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="mt-1 rounded-sm border-[#DCDCDA] focus:border-[#002FA7] focus:ring-[#002FA7]/20"
                        />
                    </div>

                    <div>
                        <Label htmlFor="password" className="text-sm font-medium text-[#0A0A0A]">Password *</Label>
                        <Input
                            id="password"
                            data-testid="input-password"
                            type="password"
                            required
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            className="mt-1 rounded-sm border-[#DCDCDA] focus:border-[#002FA7] focus:ring-[#002FA7]/20"
                        />
                        {mode === 'register' && (
                            <p className="text-xs text-[#666666] mt-1">Password must be at least 6 characters</p>
                        )}
                    </div>

                    {mode === 'register' && (
                        <div>
                            <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#0A0A0A]">Confirm Password *</Label>
                            <Input
                                id="confirmPassword"
                                data-testid="input-confirm-password"
                                type="password"
                                required
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                className="mt-1 rounded-sm border-[#DCDCDA] focus:border-[#002FA7] focus:ring-[#002FA7]/20"
                            />
                        </div>
                    )}

                    <Button
                        type="submit"
                        data-testid="submit-auth-btn"
                        disabled={loading}
                        className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm py-6 font-medium"
                    >
                        {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                    </Button>
                </form>

                <div className="mt-6 text-center">
                    <p className="text-sm text-[#666666]">
                        {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                        <button
                            onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                            data-testid="switch-auth-mode-btn"
                            className="text-[#002FA7] font-medium hover:underline"
                        >
                            {mode === 'login' ? 'Sign up' : 'Sign in'}
                        </button>
                    </p>
                </div>

                {mode === 'login' && (
                    <div className="mt-4 text-center">
                        <button
                            onClick={() => {
                                // Implement password reset functionality
                                toast.info('Password reset feature coming soon');
                            }}
                            className="text-xs text-[#666666] hover:text-[#002FA7] transition-colors"
                        >
                            Forgot password?
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthModal;